import React from "react";
import { Box, Grid, Typography, useTheme } from "@mui/material";

import Header from "../../components/Header";
import DashboardCard from "../../components/DashboardCard";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import StatusChip from "../../components/StatusChip";

import useRelationshipDashboard from "../../hooks/useRelationshipDashboard";
import { formatRelativeDate } from "../../utils/formatRelativeDate";
import { contactStatusOptions } from "../../config/exportFormOptions";
import { tokens } from "../../theme/tokens";

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { data, loading, error } = useRelationshipDashboard();

  if (loading) {
    return (
      <Box>
        <Header title="DASHBOARD" subtitle="Loading relationship insights..." />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <Header title="DASHBOARD" subtitle="Failed to load dashboard" />
      </Box>
    );
  }

  const actionRows = (data.actionRequiredContacts || []).map((contact) => ({
    id: contact.id,
    name: `${contact.firstName || ""} ${contact.lastName || ""}`.trim(),
    companyName: contact.companyName || "",
    status: contact.status || "",
    relationshipScore: contact.relationshipScore || 0,
    nextFollowUpAt: contact.nextFollowUpAt || null,
    lastMeaningfulTouchAt: contact.lastMeaningfulTouchAt || null,
    atRisk: Boolean(contact.atRisk),
  }));

  const statusBreakdownMap = (data.statusBreakdown || []).reduce((acc, item) => {
    acc[item.status] = item.count;
    return acc;
  }, {});

  const fullStatusBreakdown = contactStatusOptions.map((option) => ({
    label: option.label,
    value: statusBreakdownMap[option.value] || 0,
  }));

  const actionColumns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.3,
      minWidth: 160,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 130,
      renderCell: ({ value }) => (
        <StatusChip label={formatStatusLabel(value)} tone="default" />
      ),
    },
    {
      field: "relationshipScore",
      headerName: "Score",
      flex: 0.8,
      minWidth: 90,
      renderCell: ({ value }) => (
        <StatusChip
          label={Math.round(value || 0)}
          tone={relationshipScoreTone(value)}
        />
      ),
    },
    {
      field: "lastMeaningfulTouchAt",
      headerName: "Last Touch",
      flex: 1,
      minWidth: 120,
      renderCell: ({ value }) => formatRelativeDate(value),
    },
    {
      field: "nextFollowUpAt",
      headerName: "Next Follow-Up",
      flex: 1.1,
      minWidth: 140,
      renderCell: ({ value }) => formatRelativeDate(value),
    },
    {
      field: "companyName",
      headerName: "Company",
      flex: 1.2,
      minWidth: 140,
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="DASHBOARD"
          subtitle="System-level view of your relationship pipeline"
        />
      </Box>

      <Grid container spacing={2}>
        <DashboardCard cardTitle="Active Contacts" xs={12} sm={6} md={3} height="140px">
          <StatValue value={data.totalActiveContacts} />
        </DashboardCard>

        <DashboardCard cardTitle="Due Follow-Ups" xs={12} sm={6} md={3} height="140px">
          <StatValue value={data.dueFollowUps} />
        </DashboardCard>

        <DashboardCard cardTitle="At Risk" xs={12} sm={6} md={3} height="140px">
          <StatValue value={data.atRiskContacts} />
        </DashboardCard>

        <DashboardCard cardTitle="Average Score" xs={12} sm={6} md={3} height="140px">
          <StatValue value={Math.round(Number(data.averageRelationshipScore || 0))} />
        </DashboardCard>

        <DashboardCard cardTitle="Status Breakdown" xs={12} md={4} height="360px">
          <SimpleBarList items={fullStatusBreakdown} colors={colors} />
        </DashboardCard>

        <DashboardCard cardTitle="Score Distribution" xs={12} md={4} height="360px">
          <SimpleBarList
            colors={colors}
            items={(data.scoreDistribution || []).map((item) => ({
              label: item.rangeLabel,
              value: item.count,
            }))}
          />
        </DashboardCard>

        <DashboardCard cardTitle="Recent Interaction Activity" xs={12} md={4} height="360px">
          <SimpleActivityList items={data.recentInteractionActivity || []} colors={colors} />
        </DashboardCard>

        <DashboardCard cardTitle="Action Required" xs={12} md={9} height="420px">
          <ThemedDataGrid
            rows={actionRows}
            columns={actionColumns}
            hideFooter
            disableRowSelectionOnClick
          />
        </DashboardCard>

        <DashboardCard cardTitle="Immediate Follow-Ups" xs={12} md={3} height="420px">
          <SimpleUpcomingList items={actionRows} colors={colors} />
        </DashboardCard>
      </Grid>
    </Box>
  );
};

const relationshipScoreTone = (value) => {
  const score = Number(value || 0);

  if (score > 70) return "success";
  if (score > 40) return "gold";
  if (score > 0) return "teal";

  return "default";
};

const formatStatusLabel = (value) => {
  if (!value) return "Unknown";

  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const StatValue = ({ value }) => (
  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
    <Typography variant="h2" fontWeight="bold">
      {value}
    </Typography>
  </Box>
);

const SimpleBarList = ({ items, colors }) => {
  const maxValue = Math.max(...items.map((item) => item.value), 1);

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={1}>
      {items.map((item) => (
        <Box key={item.label}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2">{item.label}</Typography>
            <Typography variant="body2" sx={{ color: colors.scalpelTeal[300] }}>
              {item.value}
            </Typography>
          </Box>

          <Box
            height="10px"
            borderRadius="999px"
            sx={{
              bgcolor: colors.surface.elevated,
              overflow: "hidden",
            }}
          >
            <Box
              height="100%"
              width={`${(item.value / maxValue) * 100}%`}
              sx={{ bgcolor: colors.scalpelTeal[400] }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const SimpleActivityList = ({ items, colors }) => (
  <Box display="flex" flexDirection="column" gap={1} pt={1} maxHeight="100%" overflow="auto">
    {items.map((item) => (
      <Box
        key={item.date}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={1.2}
        sx={{
          bgcolor: colors.surface.card,
          border: `1px solid ${colors.surface.border}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="body2">{item.date}</Typography>

        <Box display="flex" gap={2}>
          <Typography variant="body2">Total: {item.total}</Typography>
          <Typography variant="body2" sx={{ color: colors.scalpelTeal[300] }}>
            Meaningful: {item.meaningful}
          </Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const SimpleUpcomingList = ({ items, colors }) => {
  const sortedItems = [...items]
    .filter((item) => item.nextFollowUpAt)
    .sort((a, b) => new Date(a.nextFollowUpAt) - new Date(b.nextFollowUpAt))
    .slice(0, 8);

  return (
    <Box display="flex" flexDirection="column" gap={1} pt={1} maxHeight="100%" overflow="auto">
      {sortedItems.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No immediate follow-ups.
        </Typography>
      ) : (
        sortedItems.map((item) => (
          <Box
            key={item.id}
            p={1.2}
            sx={{
              bgcolor: colors.surface.card,
              border: `1px solid ${colors.surface.border}`,
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {item.name || "Unnamed contact"}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {formatRelativeDate(item.nextFollowUpAt)}
            </Typography>

            {item.companyName ? (
              <Typography variant="caption" color="text.secondary">
                {item.companyName}
              </Typography>
            ) : null}
          </Box>
        ))
      )}
    </Box>
  );
};

export default Dashboard;